import cx from 'classnames';
import {isNumber} from 'lodash';
import {useContext, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {UserContext} from 'context';
import {UserPasswordExpireStatus} from 'enums';
import {TRANSLATION_KEYS} from 'locale';
import {User} from 'types';
import {dateUtils, userAuthUtils} from 'utils';
import {formatDate} from 'utils/transform';

type Props = {
    user: User;
    className?: string;
};

const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON;
const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.PROFILE;

export const PasswordChangedAt: React.FC<Props> = ({user, className}) => {
    const {t} = useTranslation();

    const {settings: {common: {timeZone, dateFormat, timeFormat}, registration: {userPasswordExpiration}}} = useContext(UserContext);
    const dateTimeFormat = useMemo(() => dateUtils.getDateTimeFormat(dateFormat, timeFormat), [dateFormat, timeFormat]);

    const passwordExpiresInDays = useMemo(() =>
        userAuthUtils.getUserPasswordExpiresInDays(userPasswordExpiration, user), [user, userPasswordExpiration]);

    const passwordExpirationStatus = useMemo(() =>
        userAuthUtils.getUserPasswordExpireStatus(userPasswordExpiration, timeZone, user),
    [userPasswordExpiration, timeZone, user]);

    const passwordExpiresSoon = useMemo(() =>
        passwordExpirationStatus === UserPasswordExpireStatus.ExpireSoon,
    [passwordExpirationStatus]);

    const passwordExpired = useMemo(() =>
        passwordExpirationStatus === UserPasswordExpireStatus.Expired,
    [passwordExpirationStatus]);

    return user.passwordChangedAt ? (
        <div className={cx('tw-flex tw-text-sm tw-text-default-dimmed', className)}>
            <span className='tw-mr-1'>{`${t(COMMON_T_KEYS.ALL.CHANGED)}:`}</span>
            {formatDate(user.passwordChangedAt, timeZone, dateTimeFormat)}
            {isNumber(passwordExpiresInDays) && !passwordExpired &&
                <span className={cx('tw-ml-1', {'tw-text-alert': passwordExpiresSoon})}>
                    {`(${t(T_KEYS.MESSAGES.DAYS_TO_EXPIRE)}: ${passwordExpiresInDays})`}
                </span>}
            {passwordExpired &&
                <span className={cx('tw-ml-1 tw-text-alert')}>
                    {`(${t(T_KEYS.MESSAGES.EXPIRED)})`}
                </span>}
        </div>
    ) : null;
};